/**
 * The one place that knows which provider is configured.
 *
 * WHY CONFIGURATION IS A SETTING AND NOT A CONSTANT
 * -------------------------------------------------
 * Because "sync to Google Drive" and "backup to a local folder" must be
 * independently selectable, and a user who has never heard of cloud sync must
 * find the app behaving exactly as it always has. So the configuration is two
 * independent settings, both ABSENT by default.
 *
 * With neither set, `getSyncEngine` returns null, no outbox row is read, no
 * provider is constructed, and the application is byte-for-byte the offline
 * application it was. There is no "sync mode" to be in.
 */
import type { DNRDatabase } from '@data/db/database';
import type { ProviderId } from '@domain/sync/changeModel';
import { providerRegistry } from '@platform/cloud/registry';
import type { BackupProvider, SyncProvider } from '@platform/cloud/types';
import { SyncEngine } from './SyncEngine';

export const SYNC_PROVIDER_SETTING_ID = 'app:syncProviderId';
export const BACKUP_PROVIDER_SETTING_ID = 'app:backupProviderId';

async function readProviderId(
  db: DNRDatabase,
  settingId: string,
): Promise<ProviderId | null> {
  const row = await db.settings.get(settingId);
  const value = row?.value;
  return typeof value === 'string' ? (value as ProviderId) : null;
}

/** The configured sync provider, or null when the user has not chosen one. */
export async function getSyncProvider(db: DNRDatabase): Promise<SyncProvider | null> {
  const id = await readProviderId(db, SYNC_PROVIDER_SETTING_ID);
  if (!id) return null;
  return providerRegistry.sync(id);
}

/**
 * The configured backup provider, or null.
 *
 * Deliberately a DIFFERENT setting from sync. Sharing one would make it
 * impossible to back up to a customer-owned Drive while syncing through a
 * managed cloud, which is a perfectly ordinary arrangement.
 */
export async function getBackupProvider(db: DNRDatabase): Promise<BackupProvider | null> {
  const id = await readProviderId(db, BACKUP_PROVIDER_SETTING_ID);
  if (!id) return null;
  return providerRegistry.backup(id);
}

/** The engine, or null when sync is not configured. Callers must handle null. */
export async function getSyncEngine(db: DNRDatabase): Promise<SyncEngine | null> {
  const provider = await getSyncProvider(db);
  return provider ? new SyncEngine({ db, provider }) : null;
}

export async function setSyncProvider(
  db: DNRDatabase,
  id: ProviderId | null,
  now = new Date().toISOString(),
): Promise<void> {
  await writeSetting(db, SYNC_PROVIDER_SETTING_ID, id, now);
}

export async function setBackupProvider(
  db: DNRDatabase,
  id: ProviderId | null,
  now = new Date().toISOString(),
): Promise<void> {
  await writeSetting(db, BACKUP_PROVIDER_SETTING_ID, id, now);
}

async function writeSetting(
  db: DNRDatabase,
  settingId: string,
  id: ProviderId | null,
  now: string,
): Promise<void> {
  const existing = await db.settings.get(settingId);
  await db.settings.put({
    id: settingId,
    scope: 'app',
    scopeId: 'app',
    key: settingId,
    value: id,
    updatedAt: now,
  });
  void existing;
}
